import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  PiCalculator,
  PiCaretDown,
  PiDotsThreeBold,
  PiEye,
  PiEyeSlash,
  PiMoonStars,
  PiPlus,
  PiShareNetwork,
  PiSun,
  PiUser,
} from 'react-icons/pi';
import { useTheme } from '../../ThemeContext';
import { useFinancialPrivacy } from '../../FinancialPrivacyContext';
import { getFirstAndLastName } from '../../utils';
import { getAccountDisplayName, getAccountRoleLabel, getAccountTypeKey, getAccountTypeLabel } from '../../utils/accountPresentation';
import NaraBrand from '../NaraBrand';
import AppNavigation from '../AppNavigation';
import './styles.css';

const DESKTOP_MEDIA = '(min-width: 1180px)';
const MENU_GUTTER = 12;
const MENU_WIDTH = 208;
const MOBILE_TOP_THRESHOLD = 32;
const MOBILE_DIRECTION_DELTA = 12;

const DashboardHeader = ({ currentUser, currentAccount, onOpenAccountSwitcher, onNew, onCalculator, onShare }) => {
  const navigate = useNavigate();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { valuesVisible, toggleValuesVisibility } = useFinancialPrivacy();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(() => window.scrollY > 24);
  const [desktopMenu, setDesktopMenu] = useState(() => window.matchMedia(DESKTOP_MEDIA).matches);
  const [mobileHidden, setMobileHidden] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [avatarError, setAvatarError] = useState(false);
  const menuButtonRef = useRef(null);
  const lastScrollYRef = useRef(window.scrollY);
  const directionAnchorRef = useRef(window.scrollY);
  const lastDirectionRef = useRef(null);
  const menuPanelRef = useRef(null);
  const firstName = currentUser?.displayName
    ? getFirstAndLastName(currentUser.displayName).firstName
    : 'Usuário';
  const currentAccountName = getAccountDisplayName(currentAccount);
  const accountTypeKey = getAccountTypeKey(currentAccount, currentUser?.uid);
  const accountTypeLabel = getAccountTypeLabel(currentAccount, currentUser?.uid);
  const accountRoleLabel = getAccountRoleLabel(currentAccount?.role);
  const accountSubtitle = accountTypeKey === 'shared' && currentAccount?.role
    ? [accountTypeLabel, accountRoleLabel].join(' · ')
    : accountTypeLabel;
  const darkThemeActive = resolvedTheme === 'dark';

  useEffect(() => {
    setAvatarError(false);
  }, [currentUser?.photoURL]);

  useEffect(() => {
    let frame = null;
    const updateScrolledState = () => {
      frame = null;
      const nextY = Math.max(0, window.scrollY);
      const desktop = window.matchMedia(DESKTOP_MEDIA).matches;

      if (desktop) {
        const nextScrolled = nextY > 24;
        setScrolled(nextScrolled);
        setMobileHidden(false);
        document.body.classList.toggle('nf-dashboard-header-scrolled', nextScrolled);
        lastScrollYRef.current = nextY;
        return;
      }

      setScrolled(false);
      document.body.classList.remove('nf-dashboard-header-scrolled');

      if (nextY <= MOBILE_TOP_THRESHOLD) {
        setMobileHidden(false);
        lastDirectionRef.current = null;
        directionAnchorRef.current = nextY;
        lastScrollYRef.current = nextY;
        return;
      }

      const direction = nextY > lastScrollYRef.current ? 'down' : nextY < lastScrollYRef.current ? 'up' : null;
      if (direction && direction !== lastDirectionRef.current) {
        lastDirectionRef.current = direction;
        directionAnchorRef.current = nextY;
      }
      if (direction && Math.abs(nextY - directionAnchorRef.current) >= MOBILE_DIRECTION_DELTA) {
        setMobileHidden(direction === 'down');
        directionAnchorRef.current = nextY;
      }
      lastScrollYRef.current = nextY;
    };
    const handleScroll = () => {
      if (window.matchMedia(DESKTOP_MEDIA).matches) {
        updateScrolledState();
        return;
      }
      if (frame === null) frame = window.requestAnimationFrame(updateScrolledState);
    };

    updateScrolledState();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', handleScroll);
      document.body.classList.remove('nf-dashboard-header-scrolled');
    };
  }, []);

  const desktopCompact = false;

  useEffect(() => {
    if (desktopCompact || mobileHidden) setMenuOpen(false);
  }, [desktopCompact, mobileHidden]);

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_MEDIA);
    const handleChange = (event) => setDesktopMenu(event.matches);
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  const updateMenuPosition = useCallback(() => {
    if (!desktopMenu || !menuButtonRef.current) return;
    const triggerRect = menuButtonRef.current.getBoundingClientRect();
    const panelWidth = menuPanelRef.current?.offsetWidth || MENU_WIDTH;
    const panelHeight = menuPanelRef.current?.offsetHeight || 112;
    const left = Math.min(
      Math.max(MENU_GUTTER, triggerRect.right - panelWidth),
      window.innerWidth - panelWidth - MENU_GUTTER
    );
    const preferredTop = triggerRect.bottom + 8;
    const top = Math.max(
      MENU_GUTTER,
      Math.min(preferredTop, window.innerHeight - panelHeight - MENU_GUTTER)
    );
    setMenuPosition({ top, left });
  }, [desktopMenu]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const closeOnOutside = (event) => {
      if (
        !menuPanelRef.current?.contains(event.target)
        && !menuButtonRef.current?.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    if (desktopMenu) {
      updateMenuPosition();
      window.addEventListener('resize', updateMenuPosition);
      window.addEventListener('scroll', updateMenuPosition, true);
    }
    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);

    const focusFrame = window.requestAnimationFrame(() => {
      updateMenuPosition();
      menuPanelRef.current?.querySelector('[role="menuitem"]')?.focus();
    });

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [desktopMenu, menuOpen, updateMenuPosition]);

  const runAction = (action) => {
    setMenuOpen(false);
    action();
  };

  const menuPanel = menuOpen ? (
    <div
      id="dashboard-actions-menu"
      ref={menuPanelRef}
      className={`dashboard-v2-header__menu-panel${desktopMenu ? ' dashboard-v2-header__menu-panel--portal' : ''}`}
      role="menu"
      style={desktopMenu ? menuPosition : undefined}
    >
      <button type="button" role="menuitem" onClick={() => runAction(onCalculator)}>
        <PiCalculator aria-hidden="true" />
        Calculadora
      </button>
      <button type="button" role="menuitem" onClick={() => runAction(onShare)}>
        <PiShareNetwork aria-hidden="true" />
        Compartilhar
      </button>
      <button type="button" role="menuitem" onClick={() => runAction(toggleValuesVisibility)}>
        {valuesVisible ? <PiEyeSlash aria-hidden="true" /> : <PiEye aria-hidden="true" />}
        {valuesVisible ? 'Ocultar valores' : 'Mostrar valores'}
      </button>
      <button type="button" role="menuitem" className="dashboard-v2-header__theme-menu-item" onClick={() => runAction(toggleTheme)}>
        {darkThemeActive ? <PiSun aria-hidden="true" /> : <PiMoonStars aria-hidden="true" />}
        {darkThemeActive ? 'Usar tema claro' : 'Usar tema escuro'}
      </button>
    </div>
  ) : null;

  return (
    <header className={`dashboard-v2-header${scrolled ? ' is-scrolled' : ''}${mobileHidden ? ' is-mobile-hidden' : ''}`}>
      {!desktopCompact && (
        <div className="dashboard-v2-header__identity">
          <NaraBrand size="sm" className="dashboard-v2-header__brand" />
        </div>
      )}

      <AppNavigation placement="header" />

      <div className="dashboard-v2-header__actions">

        {!desktopCompact && (
          <>
            <button type="button" className="dashboard-v2-header__new" onClick={onNew} aria-label="Nova movimentação">
              <PiPlus aria-hidden="true" />
              <span className="dashboard-v2-header__new-label-full">Nova movimentação</span>
              <span className="dashboard-v2-header__new-label-compact">Nova</span>
            </button>

            <button
              type="button"
              className="dashboard-v2-header__icon-button dashboard-v2-header__privacy-button"
              onClick={toggleValuesVisibility}
              aria-label={valuesVisible ? 'Ocultar valores' : 'Mostrar valores'}
              aria-pressed={!valuesVisible}
            >
              {valuesVisible ? <PiEye aria-hidden="true" /> : <PiEyeSlash aria-hidden="true" />}
            </button>

            <div className="dashboard-v2-header__menu">
              <button
                ref={menuButtonRef}
                type="button"
                className="dashboard-v2-header__icon-button"
                onClick={() => setMenuOpen((open) => !open)}
                aria-label="Abrir ações do Dashboard"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                aria-controls={menuOpen ? 'dashboard-actions-menu' : undefined}
              >
                <PiDotsThreeBold aria-hidden="true" />
              </button>
              {!desktopMenu && menuPanel}
            </div>
          </>
        )}



        <div className="dashboard-v2-header__user">
          <button
            type="button"
            className="dashboard-v2-header__user-account"
            onClick={(event) => {
              setMobileHidden(false);
              onOpenAccountSwitcher(event.currentTarget);
            }}
            aria-label={`Trocar conta. Atual: ${currentAccountName}`}
          >
            <span className="dashboard-v2-header__user-account-primary">
              <span className="dashboard-v2-header__greeting-prefix">Olá, </span>
              <strong>{firstName}</strong>
              <PiCaretDown aria-hidden="true" />
            </span>
            <small title={`${currentAccountName} · ${accountSubtitle}`}>{currentAccountName} · {accountSubtitle}</small>
          </button>
          <button
            type="button"
            className="dashboard-v2-header__avatar"
            onClick={() => navigate('/profile')}
            aria-label="Abrir perfil"
          >
            {currentUser?.photoURL && !avatarError ? (
              <img src={currentUser.photoURL} alt="" onError={() => setAvatarError(true)} />
            ) : (
              firstName?.charAt(0)?.toUpperCase() || <PiUser aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {!desktopCompact && desktopMenu && menuPanel && createPortal(menuPanel, document.body)}
    </header>
  );
};

DashboardHeader.propTypes = {
  currentUser: PropTypes.object,
  currentAccount: PropTypes.object,
  onOpenAccountSwitcher: PropTypes.func.isRequired,
  onNew: PropTypes.func.isRequired,
  onCalculator: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
};

export default DashboardHeader;
